import axios, { AxiosInstance } from 'axios';
import apiConfig, { API_ENDPOINTS } from '../config/apiConfig';

// Axios instance
const api: AxiosInstance = axios.create({
  baseURL: apiConfig.host,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

export interface ImageTransformRequest {
  data_uri: string; // base64 without data URL prefix
  prompt: string;
  mask_data_base64?: string | null;
}

export interface CreateTaskResponse {
  // align with backend response where result_handle is task uuid
  request_id: string;
  [k: string]: unknown;
}

export interface TaskStatusResponse {
  status: string;
  progress?: number;
  // backend may include video result_uri or inline data_uri
  result_uri?: string;
  video?: { data_uri?: string };
  // optional fields observed from backend/status
  statusMessages?: { message?: string; detail_message?: string };
  image_uri?: string;
  image_data_base64?: string;
  queue_items_count?: number;
  queue_items_ahead?: number;
  queue_items_oldest_age_seconds?: number;
  [k: string]: unknown;
}

export interface TaskResultResponse {
  video: { data_uri: string };
}

export interface TtsTaskStatusResponse extends TaskStatusResponse {
  audio?: { data_uri?: string; content_type?: string; file_name?: string };
}

export interface TtsTaskResultResponse {
  audio: { data_uri: string; content_type?: string; file_name?: string };
}

export const ttsApi = {
  async generateSpeech(
    prompt: string,
    voiceFile?: File,
    predefinedVoice?: string,
    extras?: { emotion_text?: string; speech_speed_factor?: number; audio_url?: string; audio_uri?: string },
  ): Promise<CreateTaskResponse> {
    let audio_data_uri: string | undefined = undefined;
    if (!predefinedVoice && !extras?.audio_url && !extras?.audio_uri && voiceFile) {
      const base64 = await fileToBase64(voiceFile);
      audio_data_uri = base64.split(',')[1] || '';
    }
    const body: any = { prompt };
    if (predefinedVoice) body.predefined_voice = predefinedVoice;
    else if (extras?.audio_url) body.audio_url = extras.audio_url;
    else if (extras?.audio_uri) body.audio_uri = extras.audio_uri;
    else if (audio_data_uri) body.audio_data_uri = audio_data_uri;
    if (typeof extras?.speech_speed_factor === 'number') body.speech_speed_factor = extras.speech_speed_factor;
    if (extras?.emotion_text) body.emotion_text = extras.emotion_text;
    const { data } = await api.post(API_ENDPOINTS.TTS_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string): Promise<TtsTaskStatusResponse> {
    const { data } = await api.get(API_ENDPOINTS.TTS_STATUS(taskId));
    return data as TtsTaskStatusResponse;
  },

  async getResult(taskId: string): Promise<TtsTaskResultResponse> {
    const { data } = await api.get(API_ENDPOINTS.TTS_RESULT(taskId));
    return data as TtsTaskResultResponse;
  },

  async cancelTask(taskId: string): Promise<any> {
    const { data } = await api.post(API_ENDPOINTS.TTS_CANCEL(taskId));
    return data;
  },
};

export const videoExtendApi = {
  async extendVideo(
    videoFile: File,
    prompt: string,
    queue?: string,
    duration?: number,
    resolution?: number,
    truncateAfterSeconds?: number,
  ): Promise<CreateTaskResponse> {
    const videoBase64 = await fileToBase64(videoFile);
    const payload: any = {
      data_uri: videoBase64.split(',')[1] || '',
      prompt,
      ...(queue ? { queue } : {}),
      ...(typeof duration === 'number' ? { duration } : {}),
      ...(typeof resolution === 'number' ? { min_height: resolution } : {}),
      ...(typeof truncateAfterSeconds === 'number' ? { truncate_video_after_seconds: truncateAfterSeconds } : {}),
    };
    const { data } = await api.post(API_ENDPOINTS.WAN22_EXTEND_CREATE, payload);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_EXTEND_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_EXTEND_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.WAN22_EXTEND_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const virtualTtsApi = {
  async generateSpeech(
    prompt: string,
    voiceFile?: File,
    predefinedVoice?: string,
    extras?: { emotion_text?: string; speech_speed_factor?: number; audio_url?: string; audio_uri?: string },
  ): Promise<CreateTaskResponse> {
    let audio_data_uri: string | undefined = undefined;
    if (!predefinedVoice && !extras?.audio_url && !extras?.audio_uri && voiceFile) {
      const base64 = await fileToBase64(voiceFile);
      audio_data_uri = base64.split(',')[1] || '';
    }
    const body: any = { prompt };
    if (predefinedVoice) body.predefined_voice = predefinedVoice;
    else if (extras?.audio_url) body.audio_url = extras.audio_url;
    else if (extras?.audio_uri) body.audio_uri = extras.audio_uri;
    else if (audio_data_uri) body.audio_data_uri = audio_data_uri;
    if (typeof extras?.speech_speed_factor === 'number') body.speech_speed_factor = extras.speech_speed_factor;
    if (extras?.emotion_text) body.emotion_text = extras.emotion_text;
    const { data } = await api.post(API_ENDPOINTS.VIRTUAL_TTS_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string): Promise<TtsTaskStatusResponse> {
    const { data } = await api.get(API_ENDPOINTS.VIRTUAL_TTS_STATUS(taskId));
    return data as TtsTaskStatusResponse;
  },

  async getResult(taskId: string): Promise<TtsTaskResultResponse> {
    const { data } = await api.get(API_ENDPOINTS.VIRTUAL_TTS_RESULT(taskId));
    return data as TtsTaskResultResponse;
  },

  async cancelTask(taskId: string): Promise<any> {
    const { data } = await api.post(API_ENDPOINTS.VIRTUAL_TTS_CANCEL(taskId));
    return data;
  },
};

export const imageToVideoApi = {
  async generateVideo(
    imageFile: File,
    prompt: string,
    queue?: string,
    duration?: number,
    resolution?: number,
    secondImageFile?: File,
    useNewGateway?: boolean,
  ): Promise<CreateTaskResponse> {
    const [imageBase64, secondBase64] = await Promise.all([
      fileToBase64(imageFile),
      secondImageFile ? fileToBase64(secondImageFile) : Promise.resolve(''),
    ]);
    const payload: ImageTransformRequest = {
      data_uri: imageBase64.split(',')[1] || '',
      prompt,
    };
    const body = {
      ...payload,
      ...(secondBase64 ? { second_image_uri: secondBase64.split(',')[1] || '' } : {}),
      ...(queue ? { queue } : {}),
      ...(typeof duration === 'number' ? { duration } : {}),
      ...(typeof resolution === 'number' ? { min_height: resolution } : {}),
    } as any;
    const config = useNewGateway
      ? { headers: { 'x-pipelet-mirror': 'true' } }
      : { headers: { 'x-pipelet-mirror': 'false' } };
    const { data } = await api.post(API_ENDPOINTS.WAN22_I2V_CREATE, body, config);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_I2V_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_I2V_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.WAN22_I2V_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const soundToVideoApi = {
  async generateVideo(
    imageFile: File,
    audioFile: File,
    prompt: string,
    queue?: string,
    resolution?: number,
    duration?: number,
  ): Promise<CreateTaskResponse> {
    const [imageBase64, audioBase64] = await Promise.all([fileToBase64(imageFile), fileToBase64(audioFile)]);
    const body: any = {
      data_uri: imageBase64.split(',')[1] || '',
      audio_uri: audioBase64.split(',')[1] || '',
      prompt: prompt || '',
      ...(queue ? { queue } : {}),
      // Map UI resolution selection to backend-expected field name
      ...(typeof resolution === 'number' ? { min_height: resolution } : {}),
      ...(typeof duration === 'number' ? { duration } : {}),
    };
    const { data } = await api.post(API_ENDPOINTS.WAN22_S2V_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_S2V_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_S2V_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.WAN22_S2V_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const videoLipsyncApi = {
  async generateVideo(payload: Record<string, unknown>): Promise<CreateTaskResponse> {
    const { data } = await api.post(API_ENDPOINTS.WAN21_V2V_LIPSYNC_PRO_CREATE, payload);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.WAN21_V2V_LIPSYNC_PRO_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.WAN21_V2V_LIPSYNC_PRO_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.WAN21_V2V_LIPSYNC_PRO_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const textToVideoApi = {
  async generateVideo(
    prompt: string,
    queue?: string,
    duration?: number,
    useNewGateway?: boolean,
    width?: number,
    height?: number,
  ): Promise<CreateTaskResponse> {
    const payload: ImageTransformRequest = {
      data_uri: '',
      mask_data_base64: null,
      prompt,
    };
    const body = {
      ...payload,
      ...(queue ? { queue } : {}),
      ...(typeof duration === 'number' ? { duration } : {}),
      ...(typeof width === 'number' ? { width } : {}),
      ...(typeof height === 'number' ? { height } : {}),
    } as any;
    const config = useNewGateway
      ? { headers: { 'x-pipelet-mirror': 'true' } }
      : { headers: { 'x-pipelet-mirror': 'false' } };
    const { data } = await api.post(API_ENDPOINTS.WAN22_T2V_CREATE, body, config);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_T2V_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.WAN22_T2V_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.WAN22_T2V_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const videoUpscaleApi = {
  async upscaleVideo(
    videoFile: File,
    queue?: string,
    newResolution?: string,
  ): Promise<CreateTaskResponse> {
    const videoBase64 = await fileToBase64(videoFile);
    const payload = {
      data_uri: videoBase64.split(',')[1] || '',
      prompt: '', // No prompt needed for upscaling
    };
    const body = {
      ...payload,
      ...(queue ? { queue } : {}),
      ...(newResolution ? { new_resolution: newResolution } : {}),
    } as any;
    const { data } = await api.post(API_ENDPOINTS.VIDEO_UPSCALE_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.VIDEO_UPSCALE_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.VIDEO_UPSCALE_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.VIDEO_UPSCALE_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const singingPhotoApi = {
  async generateVideo(
    imageFile: File | string,
    song_style: string,
    queue?: string,
    duration?: number,
    resolution?: number,
    lyrics?: string,
  ): Promise<CreateTaskResponse> {
    let payload: any;
    
    // If imageFile is a string (URL), use image_url instead of data_uri
    if (typeof imageFile === 'string') {
      payload = {
        image_url: imageFile,
      };
    } else {
      // If it's a File, convert to base64
      const imageBase64 = await fileToBase64(imageFile);
      payload = {
        data_uri: imageBase64.split(',')[1] || '',
      };
    }
    
    const body = {
      ...payload,
      ...(song_style ? { song_style } : {}),
      ...(lyrics ? { lyrics } : {}),
      ...(queue ? { queue } : {}),
      ...(typeof duration === 'number' ? { duration } : {}),
      ...(typeof resolution === 'number' ? { min_height: resolution } : {}),
    } as any;
    const { data } = await api.post(API_ENDPOINTS.SINGING_PHOTO_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string, queue?: string): Promise<TaskStatusResponse> {
    const endpoint =
      API_ENDPOINTS.SINGING_PHOTO_STATUS(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string, queue?: string): Promise<TaskResultResponse> {
    const endpoint =
      API_ENDPOINTS.SINGING_PHOTO_RESULT(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.get(endpoint);
    return data as TaskResultResponse;
  },

  async cancelTask(taskId: string, queue?: string): Promise<any> {
    const endpoint =
      API_ENDPOINTS.SINGING_PHOTO_CANCEL(taskId) + (queue ? `?queue=${encodeURIComponent(queue)}` : '');
    const { data } = await api.post(endpoint);
    return data;
  },
};

export const singingPhotoZeroApi = {
  async generateImage(
    prompt: string,
    width: number,
    height: number,
    randomStyle?: boolean,
  ): Promise<CreateTaskResponse> {
    const body = {
      prompt: prompt || 'random',
      width,
      height,
      batch_size: 1,
      ...(typeof randomStyle === 'boolean' ? { random_style: randomStyle } : {}),
    } as any;
    const { data } = await api.post(API_ENDPOINTS.SINGING_PHOTO_ZERO_CREATE, body);
    return data as CreateTaskResponse;
  },

  async checkStatus(taskId: string): Promise<TaskStatusResponse> {
    const { data } = await api.get(API_ENDPOINTS.SINGING_PHOTO_ZERO_STATUS(taskId));
    return data as TaskStatusResponse;
  },

  async getResult(taskId: string): Promise<TaskStatusResponse> {
    const { data } = await api.get(API_ENDPOINTS.SINGING_PHOTO_ZERO_RESULT(taskId));
    return data as TaskStatusResponse;
  },

  async cancelTask(taskId: string): Promise<any> {
    const { data } = await api.post(API_ENDPOINTS.SINGING_PHOTO_ZERO_CANCEL(taskId));
    return data;
  },
};

export default api;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = (error) => reject(error);
  });
}
